import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { isApiError } from '@/lib/api/errors';
import { requestSignInCode, signInWithCode } from '@/services/authService';

/**
 * Email a one-time code, then enter it. Used by the sign-in page ("Email me a
 * code instead") and by the blocking "Verify this device" step. Entering a
 * valid code signs in and verifies this device for two weeks.
 */
export function CodeSignIn({
  email,
  stayLoggedIn = false,
  onSignedIn,
}: {
  email: string;
  stayLoggedIn?: boolean;
  onSignedIn?: () => void;
}) {
  const [code, setCode] = useState('');
  const send = useMutation({ mutationFn: () => requestSignInCode(email), meta: { silent: true } });
  const verify = useMutation({
    mutationFn: () => signInWithCode(email, code.trim(), stayLoggedIn),
    meta: { silent: true },
    onSuccess: () => onSignedIn?.(),
  });

  const sendError = send.error ? 'Simon could not send a code right now. Try again shortly.' : null;
  const verifyError = verify.error
    ? isApiError(verify.error) && [400, 401, 410].includes(verify.error.status)
      ? 'That code is wrong or has expired. Check the latest email, or send a new code.'
      : 'Sign-in is unavailable right now. Try again shortly.'
    : null;

  if (!send.isSuccess) {
    return (
      <div>
        <p className="text-sm text-ink-soft">
          We'll email a 6-digit code to <span className="font-semibold text-ink">{email || 'your address'}</span>. It
          expires in 10 minutes.
        </p>
        {sendError && (
          <p role="alert" className="mt-3 rounded-lg bg-surface-sunken px-3 py-2 text-sm text-bronze">
            {sendError}
          </p>
        )}
        <Button type="button" className="mt-4 w-full" disabled={!email || send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? 'Sending…' : 'Email me a code'}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim().length === 6) verify.mutate();
      }}
      noValidate
    >
      <p className="text-sm text-ink-soft">
        If <span className="font-semibold text-ink">{email}</span> has an account, a code is on its way. Enter it below.
      </p>
      <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="code">
        Sign-in code
      </label>
      <input
        id="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-center font-mono text-xl tracking-[0.4em] text-ink outline-none focus:border-sage"
      />
      {verifyError && (
        <p role="alert" className="mt-3 rounded-lg bg-surface-sunken px-3 py-2 text-sm text-bronze">
          {verifyError}
        </p>
      )}
      <Button type="submit" className="mt-4 w-full" disabled={code.trim().length !== 6 || verify.isPending}>
        {verify.isPending ? 'Checking…' : 'Sign in'}
      </Button>
      <button
        type="button"
        onClick={() => {
          setCode('');
          send.mutate();
        }}
        disabled={send.isPending}
        className="mt-3 w-full text-xs font-semibold text-sage hover:underline disabled:opacity-50"
      >
        Send a new code
      </button>
    </form>
  );
}
